// # Checklist Component for Step 2 Mentorship - Quick Tasks
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import SharedHeader from '../../../components/SharedHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { 
  Box, Typography, IconButton, List, ListItem, ListItemText,
  ListItemSecondaryAction, Checkbox, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, TextField,
  DialogContentText, Fab, Tooltip, Grid, Paper,
  Tabs, Tab, Card, CardContent, Chip, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon,
  Edit as EditIcon,
  ReadMore as ReadMoreIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Today as TodayIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
// # Update to use FullCalendar
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
// # Import interact.js for drag and drop functionality
import interact from 'interactjs';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { format, isToday, parseISO, addDays } from 'date-fns';
import stepOneIcon from '../../../aneo_media/mentee/one.png';
import './checklist2.css';
// # Import library navigation button icon
import { FaBookMedical } from 'react-icons/fa';

// # Helper function to generate random ID
const generateTaskId = () => {
  const timestamp = new Date().toISOString().replace(/[-:.]/g, '');
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let uniqueId = '';
  for (let i = 0; i < 6; i++) {
    uniqueId += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `qck_tsk${timestamp}${uniqueId}`;
};

// # Helper function to check if text needs truncation
const needsTruncation = (text) => {
  if (!text) return false;
  return text.split(' ').length > 6 || text.length > 40;
};

// # Define task types and their colors
const taskTypes = {
  general: { label: 'General', color: '#3498db' },
  study: { label: 'Study', color: '#2ecc71' },
  review: { label: 'Review', color: '#f39c12' },
  practice: { label: 'Practice', color: '#9b59b6' }
};

function Checklist2({ isAdmin }) {
  const navigate = useNavigate();
  const { userProfile, user } = useAuthStore();
  const [tasks, setTasks] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [readMoreTask, setReadMoreTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    type: 'general',
    description: '',
    deadline: ''
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarExpanded, setCalendarExpanded] = useState(true);
  // # Add state for target user ID and mentor status
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);
  // # Add state for drag and drop
  const [draggedTask, setDraggedTask] = useState(null);
  
  // # Add ref for scrolling
  const taskRefs = useRef({});
  const calendarRef = useRef(null);

  // # Get the correct user ID (mentee's ID when mentor is viewing)
  useEffect(() => {
    const menteeId = sessionStorage.getItem('currentViewingMenteeId');
    setTargetUserId(menteeId || user?.uid);
    setIsMentor(!!menteeId);
  }, [user?.uid]);

  // # Load tasks from Firestore
  useEffect(() => {
    const loadTasks = async () => {
      if (!targetUserId) return;

      try {
        // # Get tasks from s2_mentee collection
        const docRef = doc(db, 's2_mentee', targetUserId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          // # Get quicktasks data or initialize empty object
          const quicktasksData = data.quicktasks || {};
          setTasks(quicktasksData);
        }
      } catch (error) {
        console.error('Error loading tasks:', error);
      }
    };

    loadTasks();
  }, [targetUserId]);

  // # Save tasks to Firestore
  const saveTasks = async (updatedTasks) => {
    if (!targetUserId) return;

    try {
      const docRef = doc(db, 's2_mentee', targetUserId);
      
      await setDoc(docRef, {
        quicktasks: updatedTasks
      }, { merge: true });
    } catch (error) {
      console.error('Error saving tasks:', error);
    }
  };

  // # Handle dialog open for new task
  const handleAddClick = () => {
    setEditingTask(null);
    setFormData({ title: '', type: 'general', description: '', deadline: '' });
    setOpenDialog(true);
  };

  // # Handle dialog open for edit task
  const handleEditClick = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      type: task.type,
      description: task.description,
      deadline: task.deadline || ''
    });
    setOpenDialog(true);
  };

  // # Handle form submit
  const handleSubmit = async () => {
    if (!formData.title) return;

    const timestamp = new Date().toISOString();
    let updatedTasks;

    if (editingTask) {
      // # Update existing task
      const { id, ...taskData } = editingTask;
      updatedTasks = {
        ...tasks,
        [id]: {
          ...taskData,
          title: formData.title,
          type: formData.type,
          description: formData.description,
          deadline: formData.deadline,
          updatedAt: timestamp
        }
      };
    } else {
      // # Add new task
      const newTaskId = generateTaskId();
      updatedTasks = {
        ...tasks,
        [newTaskId]: {
          title: formData.title,
          type: formData.type,
          description: formData.description,
          deadline: formData.deadline,
          completed: false,
          createdAt: timestamp,
          updatedAt: timestamp
        }
      };
    }

    setTasks(updatedTasks);
    await saveTasks(updatedTasks);
    setOpenDialog(false);
    setFormData({ title: '', type: 'general', description: '', deadline: '' });
    setEditingTask(null);
  };

  // # Handle task completion toggle
  const handleToggleComplete = async (taskId) => {
    const updatedTasks = {
      ...tasks,
      [taskId]: {
        ...tasks[taskId],
        completed: !tasks[taskId].completed,
        updatedAt: new Date().toISOString()
      }
    };
    setTasks(updatedTasks);
    await saveTasks(updatedTasks);
  };

  // # Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;

    const { [taskToDelete.id]: deletedTask, ...remainingTasks } = tasks;
    setTasks(remainingTasks);
    await saveTasks(remainingTasks);
    setDeleteConfirmOpen(false);
    setTaskToDelete(null);
  };

  // # Get completion status for status icon
  const getCompletionStatus = () => {
    const taskCount = Object.keys(tasks).length;
    if (taskCount === 0) return 0;
    const completedCount = Object.values(tasks).filter(task => task.completed).length;
    return (completedCount / taskCount) * 100;
  };

  // # Get stats for tasks
  const getStats = () => {
    const totalTasks = Object.entries(tasks).length;
    const completedTasks = Object.values(tasks).filter(task => task.completed).length;
    const scheduledToday = Object.values(tasks).filter(task => 
      task.deadline && isToday(new Date(task.deadline))
    ).length;
    const incompleteTasksCount = totalTasks - completedTasks;

    return {
      total: totalTasks,
      completed: completedTasks,
      incomplete: incompleteTasksCount,
      scheduledToday
    };
  };

  // # Filter tasks based on selected tab
  const getFilteredTasks = () => {
    switch (selectedTab) {
      case 1: // Completed tasks
        return Object.entries(tasks).map(([id, task]) => ({
          id,
          ...task
        })).filter(task => task.completed);
      case 2: // Incomplete tasks
        return Object.entries(tasks).map(([id, task]) => ({
          id,
          ...task
        })).filter(task => !task.completed);
      case 3: // Scheduled today
        return Object.entries(tasks).map(([id, task]) => ({
          id,
          ...task
        })).filter(task => 
          task.deadline && isToday(new Date(task.deadline))
        );
      default: // All tasks
        return Object.entries(tasks).map(([id, task]) => ({
          id,
          ...task
        }));
    }
  };

  // # Get tasks for selected date
  const getTasksForDate = (date) => {
    return Object.entries(tasks).map(([id, task]) => ({
      id,
      ...task
    })).filter(task => 
      task.deadline && format(new Date(task.deadline), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    );
  };

  // # Handle calendar date click
  const handleDateClick = (info) => {
    setSelectedDate(info.date);
    // # Filter tasks for this date and highlight them
    const tasksForDate = getTasksForDate(info.date);
    if (tasksForDate.length > 0) {
      scrollToTask(tasksForDate[0].id);
    }
  };

  // # Handle calendar event click
  const handleEventClick = (info) => {
    const taskId = info.event.id;
    if (taskId) {
      scrollToTask(taskId);
    }
  };

  // # Get stats for the dashboard
  const stats = getStats();
  
  // # Get filtered tasks based on selected tab
  const filteredTasks = getFilteredTasks();

  // # Update task deadline when dropped on a new date
  const updateTaskDeadline = async (taskId, newDate) => {
    if (!taskId || !newDate) return;
    
    try {
      // # Format the date to ISO string
      const formattedDate = format(newDate, 'yyyy-MM-dd');
      
      // # Update the task with the new deadline
      const updatedTasks = {
        ...tasks,
        [taskId]: {
          ...tasks[taskId],
          deadline: formattedDate,
          updatedAt: new Date().toISOString()
        }
      };
      
      // # Update state and save to Firestore
      setTasks(updatedTasks);
      await saveTasks(updatedTasks);
      
      // # Show feedback (optional)
      console.log(`Task ${taskId} moved to ${formattedDate}`);
    } catch (error) {
      console.error('Error updating task deadline:', error);
    }
  };

  // # Initialize drag and drop functionality
  useEffect(() => {
    // # Setup interact.js for draggable calendar events
    if (calendarExpanded) {
      setTimeout(() => {
        // # Allow time for calendar to render
        // # Exclude the checkbox from being draggable
        const draggableEvents = interact('.checklist2_calendar .fc-event:not(.checklist2_calendar_checkbox)');
        
        if (draggableEvents) {
          draggableEvents
            .draggable({
              inertia: true,
              modifiers: [
                interact.modifiers.restrictRect({
                  restriction: '.checklist2_calendar',
                  endOnly: true
                })
              ],
              autoScroll: true,
              // # Add a delay before drag starts to allow for click detection
              holdDuration: 500, // 500ms delay before drag starts
              onstart: function(event) {
                // # Don't start drag if clicked on checkbox
                if (event.target.closest('.checklist2_calendar_checkbox') || 
                    event.target.classList.contains('checklist2_calendar_checkbox')) {
                  return;
                }
                
                const taskId = event.target.getAttribute('data-task-id');
                setDraggedTask(taskId);
                event.target.classList.add('checklist2_dragging');
              },
              onmove: function(event) {
                const target = event.target;
                // # Get the position from the data-x and data-y attributes or initialize to 0
                const x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
                const y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;
                
                // # Update the element's position
                target.style.transform = `translate(${x}px, ${y}px)`;
                
                // # Store the position
                target.setAttribute('data-x', x);
                target.setAttribute('data-y', y);
              },
              onend: function(event) {
                event.target.classList.remove('checklist2_dragging');
                // # Reset the transform
                event.target.style.transform = '';
                event.target.removeAttribute('data-x');
                event.target.removeAttribute('data-y');
              }
            });
        }
        
        // # Make calendar days droppable
        const droppableDays = interact('.checklist2_calendar .fc-daygrid-day');
        
        if (droppableDays) {
          droppableDays
            .dropzone({
              accept: '.fc-event',
              overlap: 0.5,
              ondropactivate: function(event) {
                event.target.classList.add('checklist2_drop_active');
              },
              ondragenter: function(event) {
                event.target.classList.add('checklist2_drop_target');
                event.relatedTarget.classList.add('checklist2_can_drop');
              },
              ondragleave: function(event) {
                event.target.classList.remove('checklist2_drop_target');
                event.relatedTarget.classList.remove('checklist2_can_drop');
              },
              ondrop: function(event) {
                const taskId = event.relatedTarget.getAttribute('data-task-id');
                const dateAttr = event.target.getAttribute('data-date');
                
                if (taskId && dateAttr) {
                  const newDate = new Date(dateAttr);
                  updateTaskDeadline(taskId, newDate);
                }
              },
              ondropdeactivate: function(event) {
                event.target.classList.remove('checklist2_drop_active');
                event.target.classList.remove('checklist2_drop_target');
              }
            });
        }
      }, 500); // # Give time for calendar to render
    }
    
    // # Cleanup function
    return () => {
      interact('.checklist2_calendar .fc-event').unset();
      interact('.checklist2_calendar .fc-daygrid-day').unset();
    };
  }, [calendarExpanded, tasks]); // # Re-initialize when calendar is expanded or tasks change

  // # Scroll to task helper function
  const scrollToTask = (taskId) => {
    if (taskRefs.current[taskId]) {
      taskRefs.current[taskId].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      // # Highlight the task briefly
      taskRefs.current[taskId].style.backgroundColor = '#fff3cd';
      setTimeout(() => {
        taskRefs.current[taskId].style.backgroundColor = '';
      }, 2000);
    }
  };

  // # Navigate to library function
  const navigateToLibrary = () => {
    navigate('/library/step2');
  };

  return (
    <div className="checklist2_container">
      <SharedHeader />
      {/* # Library Navigation Button */}
      <div className="checklist2_library_nav_button" onClick={navigateToLibrary} title="Go to Step 2 Library">
        <FaBookMedical />
        <span>Library</span>
      </div>
      <div className="checklist2_content">
        <div className="checklist2_header">
          <Typography variant="h4" component="h1" className="checklist2_title">
            Quick Tasks Checklist {isMentor && <span className="checklist2_mentor_pill">Mentor</span>}
          </Typography>
          <Fab
            color="primary"
            aria-label="add"
            onClick={handleAddClick}
            className="checklist2_add_button"
            size="medium"
          >
            <AddIcon />
          </Fab>
        </div>

        {/* Calendar Section */}
        <div className="checklist2_calendar_section">
          <div 
            className="checklist2_calendar_header"
            onClick={() => setCalendarExpanded(!calendarExpanded)}
          >
            <div className="checklist2_calendar_title">
              <TodayIcon />
              <Typography variant="h6">Calendar View</Typography>
            </div>
            <IconButton
              className={`checklist2_expand_icon ${calendarExpanded ? 'expanded' : ''}`}
            >
              <ExpandMoreIcon />
            </IconButton>
          </div>
          {calendarExpanded && (
            <div className="checklist2_calendar_content">
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,listWeek'
                }}
                events={filteredTasks.map(task => ({
                  id: task.id,
                  title: task.title,
                  start: task.deadline,
                  allDay: true,
                  backgroundColor: task.completed ? '#a8e6cf' : '#ffb7c5',
                  borderColor: task.completed ? '#8ed4b8' : '#ff9eaf',
                  textColor: '#2c3e50',
                  classNames: ['checklist2_calendar_event', task.completed ? 'completed' : 'pending'],
                  extendedProps: {
                    status: task.completed ? 'completed' : 'pending',
                    taskId: task.id
                  }
                }))}
                height="auto"
                dayMaxEvents={false}
                eventDisplay="block"
                dayMaxEventRows={false}
                expandRows={true}
                eventContent={(eventInfo) => {
                  return (
                    <div 
                      className="checklist2_calendar_event_content"
                      data-task-id={eventInfo.event.extendedProps.taskId}
                    >
                      <Checkbox
                        checked={eventInfo.event.extendedProps.status === 'completed'}
                        onChange={(e) => {
                          // # Stop propagation to prevent drag event from triggering
                          e.stopPropagation();
                          handleToggleComplete(eventInfo.event.extendedProps.taskId);
                        }}
                        size="small"
                        className="checklist2_calendar_checkbox"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span>{eventInfo.event.title}</span>
                    </div>
                  );
                }}
                eventTimeFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  meridiem: false
                }}
                // # Add event render hook to make events draggable
                eventDidMount={(info) => {
                  // # Add task ID as data attribute for drag and drop
                  info.el.setAttribute('data-task-id', info.event.extendedProps.taskId);
                  
                  // # Find and exclude checkbox from drag behavior
                  const checkbox = info.el.querySelector('.checklist2_calendar_checkbox');
                  if (checkbox) {
                    checkbox.addEventListener('mousedown', (e) => {
                      e.stopPropagation();
                    });
                  }
                  
                  // # Add click handler to navigate to task
                  let pressTimer;
                  let isDragging = false;
                  
                  info.el.addEventListener('mousedown', (e) => {
                    // # Skip if clicked on checkbox
                    if (e.target.closest('.checklist2_calendar_checkbox') || 
                        e.target.classList.contains('checklist2_calendar_checkbox')) {
                      return;
                    }
                    
                    // # Set a timer to determine if this is a click or drag
                    pressTimer = setTimeout(() => {
                      // # After delay, mark as dragging
                      isDragging = true;
                    }, 200); // 200ms delay before considering it a drag
                  });
                  
                  info.el.addEventListener('mouseup', (e) => {
                    // # Clear the timer
                    clearTimeout(pressTimer);
                    
                    // # If not dragging, treat as a click and navigate to task
                    if (!isDragging) {
                      const taskId = info.event.extendedProps.taskId;
                      scrollToTask(taskId);
                    }
                    
                    // # Reset dragging state
                    isDragging = false;
                  });
                  
                  info.el.addEventListener('mousemove', () => {
                    // # If mouse moves while pressed, clear timer and consider it a drag
                    clearTimeout(pressTimer);
                    isDragging = true;
                  });
                }}
                // # Add day render hook to make days droppable
                dayCellDidMount={(info) => {
                  // # Add date as data attribute for drop target
                  info.el.setAttribute('data-date', info.date.toISOString().split('T')[0]);
                }}
                // # Enable event dragging with FullCalendar's built-in functionality
                editable={true}
                // # Handle event drop through FullCalendar's API as a fallback
                eventDrop={(info) => {
                  const taskId = info.event.extendedProps.taskId;
                  const newDate = info.event.start;
                  updateTaskDeadline(taskId, newDate);
                }}
              />
            </div>
          )}
        </div>

        {/* Stats Dashboard */}
        <div className="checklist2_stats_section">
          <Typography variant="h6" className="checklist2_section_title">
            Overview
          </Typography>
          <div className="checklist2_stats_grid">
            <div className="checklist2_stat_card total">
              <AssignmentIcon className="checklist2_stat_icon" />
              <Typography className="checklist2_stat_title">Total Tasks</Typography>
              <Typography className="checklist2_stat_value">{stats.total}</Typography>
            </div>
            <div className="checklist2_stat_card completed">
              <CheckCircleIcon className="checklist2_stat_icon" />
              <Typography className="checklist2_stat_title">Completed</Typography>
              <Typography className="checklist2_stat_value">{stats.completed}</Typography>
            </div>
            <div className="checklist2_stat_card pending">
              <ScheduleIcon className="checklist2_stat_icon" />
              <Typography className="checklist2_stat_title">Pending</Typography>
              <Typography className="checklist2_stat_value">{stats.incomplete}</Typography>
            </div>
            <div className="checklist2_stat_card today">
              <TodayIcon className="checklist2_stat_icon" />
              <Typography className="checklist2_stat_title">Today's Tasks</Typography>
              <Typography className="checklist2_stat_value">{stats.scheduledToday}</Typography>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="checklist2_tabs_container">
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="fullWidth"
            className="checklist2_tabs"
          >
            <Tab className="checklist2_tab" label="All Tasks" />
            <Tab className="checklist2_tab" label="Completed" />
            <Tab className="checklist2_tab" label="Incomplete" />
            <Tab className="checklist2_tab" label="Today" />
          </Tabs>
        </div>

        {/* Task List */}
        <div className="checklist2_step_section">
          <div className="checklist2_step_header">
            <img 
              src={stepOneIcon}
              alt="Step 1"
              className="checklist2_step_icon"
            />
            <Typography variant="h6" component="h2" className="checklist2_step_title">
              Quick Tasks
            </Typography>
          </div>
          
          <div className="checklist2_step_content">
            {/* Task List */}
            <List className="checklist2_list">
              {filteredTasks.length > 0 ? (
                filteredTasks.map((task) => (
                  <ListItem
                    key={task.id}
                    className={`checklist2_item ${task.completed ? 'completed' : ''}`}
                    ref={el => taskRefs.current[task.id] = el}
                  >
                    <div className="checklist2_item_checkbox">
                      <Checkbox
                        checked={task.completed || false}
                        onChange={() => handleToggleComplete(task.id)}
                        className="checklist2_checkbox"
                      />
                    </div>
                    <div className="checklist2_item_main">
                      <Typography className="checklist2_task_title" variant="subtitle1">
                        {task.title}
                      </Typography>
                    </div>
                    <Chip
                      label={taskTypes[task.type]?.label || 'General'}
                      className={`checklist2_type_chip ${task.type}`}
                      style={{
                        backgroundColor: taskTypes[task.type]?.color || taskTypes.general.color,
                        color: 'white'
                      }}
                    />
                    <div className="checklist2_item_details">
                      {task.description && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setReadMoreTask(task)}
                          className="checklist2_read_more_btn"
                          startIcon={<ReadMoreIcon />}
                        >
                          Read More
                        </Button>
                      )}
                      {task.deadline && (
                        <Typography className="checklist2_item_deadline">
                          {format(new Date(task.deadline), 'MMM dd, yyyy')}
                        </Typography>
                      )}
                    </div>
                    <div className="checklist2_item_actions">
                      <IconButton
                        onClick={() => handleEditClick(task)}
                        className="checklist2_edit_button"
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setTaskToDelete(task);
                          setDeleteConfirmOpen(true);
                        }}
                        className="checklist2_delete_button"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </ListItem>
                ))
              ) : (
                <Typography className="checklist2_empty_message">
                  No tasks found. Add quick tasks to track them.
                </Typography>
              )}
            </List>
          </div>
        </div>
      </div>

      <DashboardFooter />

      {/* Dialogs */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        className="checklist2_dialog"
      >
        <DialogTitle className="checklist2_dialog_title">
          {editingTask ? 'Edit Task' : 'Add New Task'}
        </DialogTitle>
        <DialogContent className="checklist2_dialog_content">
          <DialogContentText>
            Enter the task details below.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Task Title"
            type="text"
            fullWidth
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="checklist2_input"
          />
          <FormControl fullWidth margin="dense" className="checklist2_input">
            <InputLabel>Task Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              label="Task Type"
            >
              {Object.entries(taskTypes).map(([value, { label }]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Deadline"
            type="date"
            fullWidth
            value={formData.deadline}
            onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
            InputLabelProps={{
              shrink: true,
            }}
            className="checklist2_input"
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            type="text"
            fullWidth
            multiline
            minRows={2}
            maxRows={8}
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            className="checklist2_input checklist2_description_input"
          />
        </DialogContent>
        <DialogActions className="checklist2_dialog_actions">
          <Button onClick={() => setOpenDialog(false)} className="checklist2_dialog_button">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained" className="checklist2_dialog_button">
            {editingTask ? 'Save Changes' : 'Add Task'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        className="checklist2_delete_dialog"
      >
        <DialogTitle className="checklist2_dialog_title">Confirm Delete</DialogTitle>
        <DialogContent className="checklist2_dialog_content">
          <DialogContentText>
            Are you sure you want to delete this task? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions className="checklist2_dialog_actions">
          <Button onClick={() => setDeleteConfirmOpen(false)} className="checklist2_dialog_button">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" className="checklist2_dialog_button">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!readMoreTask}
        onClose={() => setReadMoreTask(null)}
        className="checklist2_read_more_dialog"
      >
        <DialogTitle className="checklist2_dialog_title">
          {readMoreTask?.title}
        </DialogTitle>
        <DialogContent className="checklist2_dialog_content">
          {readMoreTask?.deadline && (
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              Date: {format(new Date(readMoreTask.deadline), 'MMMM dd, yyyy')}
            </Typography>
          )}
          <Typography variant="body1" className="checklist2_description_text">
            {readMoreTask?.description || 'No description provided.'}
          </Typography>
        </DialogContent>
        <DialogActions className="checklist2_dialog_actions">
          <Button onClick={() => setReadMoreTask(null)} className="checklist2_dialog_button">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Checklist2;
